import { SelectChangeEvent } from "@mui/material/Select";
import { ChangeEvent, useEffect, Dispatch, useReducer } from "react";
import dayjs from "dayjs";

import {
  CustomSelect,
  Text,
  CustomInput,
  MultipleSelect,
} from "../../../shared/uiComponents";
import { validateName } from "../../../shared/Helpers/functions";
import {
  useBehaviorTreatmentModificationOptions,
  useIsTelehealth,
} from "../hook";
import {
  SignatureInput,
  Targets,
  Behaviors,
  TimeInfo,
  TelehealthLocations,
} from "../components";

import {
  ACTIONS,
  ActionProperties,
  ERRORS,
  behaviorTreatmentModificationErrorReducer,
  initialBehaviorTreatmentModificationErrorState,
} from "./behaviorTreatmentModificationReducer";
import { useIsActionAllowed } from "../helpers";

import { useSelector } from "../../../redux/store";
import { UpdateBehaviorTreatmentModificationProperties } from "../../../redux/API/ClientAPIHelpers/soapNoteProperties";

const BehaviorTreatmentModificationData = ({
  data,
  setData,
  save,
}: {
  data: UpdateBehaviorTreatmentModificationProperties;
  setData: Dispatch<ActionProperties>;
  save: () => void;
}) => {
  const today = new Date();
  const todayString = dayjs(today).format("MM/DD/YYYY");

  const soapNoteStatus = useSelector(
    (state) => state.soapNote.soapNote?.status?.status
  );
  const userId = useSelector(
    (state) => state.soapNote.soapNote?.user?.id
  );
  const locationList = useSelector((state) => state.soapNote.locations);
  const errorList = useSelector((state) => state.soapNote.error?.errors);
  
  const isTelehealth = useIsTelehealth(data.locationId);
  const disabled = !useIsActionAllowed(soapNoteStatus, userId);
  const {
    barriersList,
    interventionsList,
    reinforcersList,
    responseToInterventionList,
    participantsList,
  } = useBehaviorTreatmentModificationOptions();

  const [errors, setErrors] = useReducer(
    behaviorTreatmentModificationErrorReducer,
    initialBehaviorTreatmentModificationErrorState
  );

  useEffect(() => {
    if (!!data.participantResponses.length) return;
    const payload = participantsList
      .filter((x) => x.isDefault)
      .map((y) => y.id);
    setData({ type: ACTIONS.setParticipants, payload });
  }, [participantsList, data, setData]);

  useEffect(() => {
    if (!errorList) return;
    const list = Object.keys(errorList);
    list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
  }, [errorList]);

  const locationHandler = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setLocationId });
    if (!errors.LocationId) return;
    setErrors({ payload: false, type: ERRORS.LocationId });
  };

  const interventionHandler = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    if (typeof value === "string") return;
    setData({ payload: value, type: ACTIONS.setIntervention });
    if (!errors.BehaviorTreatmentModificationInterventionResponses) return;
    setErrors({
      payload: false,
      type: ERRORS.BehaviorTreatmentModificationInterventionResponses,
    });
  };

  const responseToIntervationHandler = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setResponseToIntervation });
    if (!errors.ResponseToIntervention) return;
    setErrors({ payload: false, type: ERRORS.ResponseToIntervention });
  };

  const reinforcementHandler = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    if (typeof value === "string") return;
    setData({ payload: value, type: ACTIONS.setReinforcement });
    if (!errors.ReinforcementResponses) return;
    setErrors({ payload: false, type: ERRORS.ReinforcementResponses });
  };

  const barriersHandler = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    if (typeof value === "string") return;
    setData({ payload: value, type: ACTIONS.setBarriers });
    if (!errors.BarrierToTreatmentResponses) return;
    setErrors({ payload: false, type: ERRORS.BarrierToTreatmentResponses });
  };

  const goalAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setGoalsAddressed });
    if (!errors.GoalsAddressed) return;
    setErrors({ payload: false, type: ERRORS.GoalsAddressed });
  };

  const dataAnalyzedHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setDataAnalyzed });
    if (!errors.DataAnalyzed) return;
    setErrors({ payload: false, type: ERRORS.DataAnalyzed });
  };

  const treatmentPlanUpdatesHandler = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setTreatmentPlanUpdates });
    if (!errors.TreatmentPlanUpdates) return;
    setErrors({ payload: false, type: ERRORS.TreatmentPlanUpdates });
  };

  const malaptiveBehaviorsHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setMaladaptiveBehaviorsObserved });
    if (!errors.MaladaptiveBehaviorsObserved) return;
    setErrors({ payload: false, type: ERRORS.MaladaptiveBehaviorsObserved });
  };

  const sessionSummaryHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setSessionSummary });
    if (!errors.SessionSummary) return;
    setErrors({ payload: false, type: ERRORS.SessionSummary });
  };

  const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
    if (!errors.Signature) return;
    setErrors({ payload: false, type: ERRORS.Signature });
  };

  const validateSignatureHandler = () => {
    if (validateName(data.signature)) {
      save();
      return;
    }
    setErrors({ payload: true, type: ERRORS.Signature });
  };

  const participantsHandler = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    if (typeof value === "string") return;
    setData({ type: ACTIONS.setParticipants, payload: value });
    if (!errors.ParticipantResponses) return;
    setErrors({ payload: false, type: ERRORS.ParticipantResponses });
  };

  const barrierToTreatmentOtherHandler = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setData({ payload: value, type: ACTIONS.setBarrierToTreatmentOther });
    if (!errors.BarrierToTreatmentOther) return;
    setErrors({ payload: false, type: ERRORS.BarrierToTreatmentOther });
  };

  const isOtherSelectedForBarriersToTreatment =
    data.barrierToTreatmentResponses.find(
      (x) => barriersList.find((y) => y.id === x)?.text === "Other"
    );

  return (
    <div>
      <TimeInfo disabled={disabled} save={save} />
      <CustomSelect
        label="Location:"
        data={locationList}
        value={!!data.locationId ? data.locationId : ""}
        setValue={locationHandler}
        className={"marginBottom16"}
        error={errors.LocationId}
        disabled={disabled}
        onBlur={save}
      />
      {isTelehealth && (
        <TelehealthLocations
          setData={setData}
          data={data}
          disabled={disabled}
          save={save}
        />
      )}
      <MultipleSelect
        label="Session Participants:"
        data={participantsList}
        initialValue={data.participantResponses}
        setValue={participantsHandler}
        error={errors.ParticipantResponses}
        className="marginBottom16"
        disabled={disabled}
        onBlur={save}
      />
      <MultipleSelect
        label="Intervention in Session:"
        data={interventionsList}
        initialValue={data.behaviorTreatmentModificationInterventionResponses}
        setValue={interventionHandler}
        error={errors.BehaviorTreatmentModificationInterventionResponses}
        className="marginBottom16"
        disabled={disabled}
        onBlur={save}
      />
      <CustomSelect
        label="Response to intervention: "
        data={responseToInterventionList}
        value={`${data.responseToIntervention}`}
        setValue={responseToIntervationHandler}
        className="marginBottom16"
        error={errors.ResponseToIntervention}
        disabled={disabled}
        onBlur={save}
      />
      <MultipleSelect
        label="Reinforcers used during session:"
        initialValue={data.reinforcementResponses}
        data={reinforcersList}
        setValue={reinforcementHandler}
        className="marginBottom16"
        error={errors.ReinforcementResponses}
        disabled={disabled}
        onBlur={save}
      />
      <MultipleSelect
        label="Barriers to Treatment:"
        data={barriersList}
        initialValue={data.barrierToTreatmentResponses}
        setValue={barriersHandler}
        className={"marginBottom16"}
        error={errors.BarrierToTreatmentResponses}
        disabled={disabled}
        onBlur={save}
      />
      {isOtherSelectedForBarriersToTreatment && (
        <CustomInput
          label="If other, please describe: "
          value={data.barrierToTreatmentOther}
          setValue={barrierToTreatmentOtherHandler}
          className="marginBottom16"
          multiline={{
            multiline: true,
            rowCount: 3,
          }}
          disabled={disabled}
          onBlur={save}
          error={errors.BarrierToTreatmentOther}
        />
      )}
      <CustomInput
        label="Goals addressed:"
        value={data.goalsAddressed}
        setValue={goalAddressedHandler}
        multiline={{ multiline: true, rowCount: 3 }}
        className="marginBottom16"
        error={errors.GoalsAddressed}
        disabled={disabled}
        onBlur={save}
      />
      <CustomInput
        label="Data analyzed:"
        value={data.dataAnalyzed}
        setValue={dataAnalyzedHandler}
        multiline={{ multiline: true, rowCount: 3 }}
        className="marginBottom16"
        error={errors.DataAnalyzed}
        disabled={disabled}
        onBlur={save}
      />
      <CustomInput
        label="Updates to treatment plan:"
        value={data.treatmentPlanUpdates}
        setValue={treatmentPlanUpdatesHandler}
        multiline={{ multiline: true, rowCount: 3 }}
        className="marginBottom16"
        error={errors.TreatmentPlanUpdates}
        disabled={disabled}
        onBlur={save}
      />
      <CustomInput
        label="Maladaptive behaviors observed"
        value={data.maladaptiveBehaviorsObserved}
        setValue={malaptiveBehaviorsHandler}
        multiline={{ multiline: true, rowCount: 3 }}
        className="marginBottom16"
        error={errors.MaladaptiveBehaviorsObserved}
        disabled={disabled}
        onBlur={save}
      />
      <Text
        title="Session Details"
        size="smallBold"
        className="marginBottom16 title"
      />

      <Targets />
      <Behaviors />
      <CustomInput
        label="Session Summary: "
        value={data.sessionSummary}
        setValue={sessionSummaryHandler}
        className="marginBottom8"
        multiline={{
          multiline: true,
          rowCount: 3,
        }}
        error={errors.SessionSummary}
        disabled={disabled}
        onBlur={save}
      />
      <div style={{ width: "250px" }}>
        <CustomInput
          label="Printed Name:"
          value={data.printedName.toUpperCase()}
          setValue={() => { }}
          disabled={true}
          className="marginBottom8 signature"
        />
        <SignatureInput
          value={data.signature}
          error={errors.Signature}
          setValue={signatureHandler}
          disabled={disabled}
          onBlur={validateSignatureHandler}
        />
      </div>
      <div>
        <Text title="Date of signature: " size="smallBold" />
        <Text title={todayString} />
      </div>
    </div>
  );
};

export default BehaviorTreatmentModificationData;
